import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { MetricCard } from '../components/MetricCard';
import { ScoreRing } from '../components/ScoreRing';
import { calculateComfort } from '../engine/comfort';
import { getLocations } from '../services/data';
import { getJakartaWeather } from '../services/weather';
import type { LocationItem, WeatherSnapshot } from '../types';
import morningRun from '../assets/morning-run.webp';
import activePark from '../assets/active-park.webp';

const quickMenus = [
  { title: 'Peta Kenyamanan', helper: 'Cari lokasi yang lebih nyaman untuk beraktivitas.', to: '/app/map', icon: '⌖' },
  { title: 'Analisis Aktivitas', helper: 'Hubungkan aktivitas Strava dengan konteks lingkungan.', to: '/app/activity', icon: '↗' },
  { title: 'Story Maker', helper: 'Buat konten edukatif untuk dibagikan.', to: '/app/story', icon: '▣' },
  { title: 'Aksi & Komunitas', helper: 'Temukan gerakan warga dan tantangan mingguan.', to: '/app/actions', icon: '✦' },
];

export function HomePage() {
  const [weather, setWeather] = useState<WeatherSnapshot | null>(null);
  const [locations, setLocations] = useState<LocationItem[]>([]);

  useEffect(() => {
    void Promise.all([getJakartaWeather(), getLocations()]).then(([weatherData, locationData]) => {
      setWeather(weatherData);
      setLocations(locationData);
    });
  }, []);

  const comfort = useMemo(() => weather ? calculateComfort(weather, 74) : null, [weather]);
  const greeting = new Intl.DateTimeFormat('id-ID', { weekday: 'long' }).format(new Date());
  const topLocations = locations.slice(0, 3);
  const bestLocation = topLocations[0];

  return (
    <div className="app-page home-page">
      <header className="app-page__header">
        <div>
          <span className="eyebrow">{greeting}, Jakarta</span>
          <h1>Dashboard kenyamanan kota yang terasa lebih ringkas, informatif, dan menarik.</h1>
        </div>
        <span className={`data-status data-status--${weather?.source ?? 'demo'}`}>
          {weather?.source === 'live' ? '● Data live' : weather?.source === 'cache' ? '● Data tersimpan' : '● Mode demo'}
        </span>
      </header>

      <section className="home-overview-grid">
        <article className="home-hero-card">
          <div className="home-hero-card__text">
            <span className="eyebrow eyebrow--light">Comfort overview</span>
            <h2>{comfort && comfort.score >= 70 ? 'Hari ini cukup nyaman untuk bergerak di pagi dan sore hari.' : 'Hari ini perlu strategi waktu dan lokasi yang lebih cermat.'}</h2>
            <p>
              JakComfort membantu warga Jakarta memilih waktu, tempat, dan kebiasaan yang lebih nyaman, sehat, dan ramah iklim.
            </p>
            <div className="time-pills">
              <span><strong>06.00–09.00</strong> Waktu disarankan</span>
              <span><strong>16.00–18.00</strong> Alternatif sore</span>
            </div>
          </div>
          <div className="home-hero-card__visual">
            <ScoreRing score={comfort?.score ?? 81} label={comfort?.label ?? 'Nyaman'} size="md" />
            <img src={morningRun} alt="Aktivitas pagi di ruang hijau Jakarta" />
          </div>
        </article>

        <article className="compact-panel compact-panel--stack">
          <div className="compact-panel__header">
            <div>
              <span className="eyebrow">Ringkasan cepat</span>
              <h3>Status lingkungan hari ini</h3>
            </div>
          </div>
          <div className="mini-stat-list">
            <div><strong>{weather?.apparentTemperature ?? 29}°C</strong><span>Suhu terasa</span></div>
            <div><strong>AQI {weather?.usAqi ?? 54}</strong><span>Kualitas udara</span></div>
            <div><strong>74/100</strong><span>Area hijau</span></div>
          </div>
          <div className="alert-list">
            <div className="alert-item"><span>☀</span><p>Siang cenderung lebih panas. Pilih rute teduh bila beraktivitas setelah pukul 10.00.</p></div>
            <div className="alert-item"><span>♧</span><p>Ruang hijau memberi efek teduh, membantu kenyamanan, dan dapat menjadi sarana edukasi iklim warga.</p></div>
          </div>
        </article>
      </section>

      <section className="metric-grid metric-grid--home">
        <MetricCard icon="☀" label="Suhu terasa" value={`${weather?.apparentTemperature ?? 29}°C`} helper={comfort?.temperatureScore && comfort.temperatureScore > 70 ? 'Masih nyaman' : 'Perlu strategi'} tone="orange" />
        <MetricCard icon="≋" label="Kualitas udara" value={`AQI ${weather?.usAqi ?? 54}`} helper={comfort?.airScore && comfort.airScore > 70 ? 'Baik untuk aktivitas' : 'Batasi intensitas'} tone="blue" />
        <MetricCard icon="♧" label="Kehijauan kota" value="74/100" helper="Cukup tinggi" tone="green" />
        <MetricCard icon="☂" label="Peluang hujan" value={`${weather?.precipitationProbability ?? 18}%`} helper="Periksa sebelum berangkat" tone="neutral" />
      </section>

      <section className="home-dashboard-grid">
        <article className="compact-panel compact-panel--featured">
          <div className="compact-panel__header">
            <div>
              <span className="eyebrow">Rekomendasi utama</span>
              <h3>Lokasi yang paling nyaman saat ini</h3>
            </div>
            <Link className="text-link" to="/app/map">Buka peta</Link>
          </div>
          {bestLocation && (
            <div className="recommendation-card">
              <img src={activePark} alt="Ruang terbuka hijau dan aktivitas warga" />
              <div>
                <span className="recommendation-card__badge">Skor {bestLocation.comfortScore}</span>
                <h4>{bestLocation.name}</h4>
                <p>{bestLocation.reason}</p>
                <div className="recommendation-card__meta">
                  <span>{bestLocation.bestTimes[0]}</span>
                  <span>{bestLocation.activities[0]}</span>
                  <span>{bestLocation.airLabel}</span>
                </div>
              </div>
            </div>
          )}
          <div className="place-card-grid place-card-grid--compact">
            {topLocations.slice(1).map((location) => (
              <Link className="place-card" to={`/app/map?place=${location.id}`} key={location.id}>
                <div className="place-card__score">{location.comfortScore}</div>
                <div><h3>{location.name}</h3><p>{location.reason}</p><span>{location.bestTimes[0]} · {location.activities[0]}</span></div>
              </Link>
            ))}
          </div>
        </article>

        <article className="compact-panel">
          <div className="compact-panel__header">
            <div>
              <span className="eyebrow">Quick access</span>
              <h3>Konsep UI tiap menu</h3>
            </div>
          </div>
          <div className="quick-menu-grid">
            {quickMenus.map((menu) => (
              <Link key={menu.title} to={menu.to} className="quick-menu-card">
                <span>{menu.icon}</span>
                <strong>{menu.title}</strong>
                <p>{menu.helper}</p>
              </Link>
            ))}
          </div>
        </article>
      </section>

      <section className="home-bottom-grid">
        <article className="insight-card insight-card--green">
          <span className="eyebrow eyebrow--light">Nilai besar</span>
          <h3>JakComfort bukan sekadar explorer, tapi alat edukasi iklim berbasis aktivitas harian.</h3>
          <p>Setiap interaksi mendorong warga memahami panas perkotaan, kualitas udara, ruang hijau, dan aksi sederhana yang relevan dengan kehidupan sehari-hari di Jakarta.</p>
          <Link to="/app/learn">Masuk ke menu edukasi →</Link>
        </article>
        <article className="compact-panel compact-panel--action">
          <div className="compact-panel__header">
            <div>
              <span className="eyebrow">Aksi minggu ini</span>
              <h3>Tiga langkah kecil yang bisa dilakukan</h3>
            </div>
          </div>
          <ol className="simple-action-list">
            <li><strong>Pilih jam yang lebih nyaman.</strong><span>Gunakan rekomendasi pagi atau sore untuk aktivitas luar ruang.</span></li>
            <li><strong>Kunjungi satu ruang hijau.</strong><span>Amati manfaat teduh, udara, dan fungsi sosialnya.</span></li>
            <li><strong>Bagikan satu insight.</strong><span>Gunakan Story Maker agar edukasi iklim menyebar lebih luas.</span></li>
          </ol>
        </article>
      </section>
    </div>
  );
}
